import React from 'react';
import { Fingerprint } from 'lucide-react';
import './FingerprintScanner.css';

const STATUS_LABELS = {
  loading:        'Loading device configuration…',
  not_configured: 'Device not configured. Enable in Settings → Biometric Device.',
  ready:          'Place your finger on the reader',
  scanning:       'Scanning… hold still',
  error:          null,
  success:        'Fingerprint recognised',
};

const STATUS_PILLS = {
  loading:        'Loading…',
  not_configured: 'Not configured',
  ready:          'Device ready',
  scanning:       'Scanning…',
  error:          'Error',
  success:        'Success',
};

/**
 * FingerprintScanner
 *
 * Props:
 *   status        — from useBiometric().deviceStatus ('loading'|'not_configured'|'ready'|'scanning'|'error'|'success')
 *   error         — from useBiometric().error (string or null)
 *   onScan        — callback when user clicks "Scan Fingerprint" button
 *   simulateMode  — shows yellow "Simulate mode" badge; only show in dev
 *   disabled      — disables the scan button
 *   label         — override default label text
 *   compact       — smaller layout (no outer padding)
 */
export default function FingerprintScanner({
  status = 'not_configured',
  error = null,
  onScan,
  simulateMode = false,
  disabled = false,
  label,
  compact = false,
}) {
  const isDisabled = disabled || status === 'loading' || status === 'not_configured' || status === 'scanning';
  const displayLabel = label ?? (status === 'error' ? error : STATUS_LABELS[status]);
  const pillStatus = status === 'error' ? 'error' : status;

  return (
    <div className={`fp-scanner${compact ? ' fp-compact' : ''}`}>
      <div className="fp-icon-wrap">
        <Fingerprint
          size={64}
          strokeWidth={1.25}
          className={`fp-icon status-${status}`}
        />
        {status === 'scanning' && <span className="fp-ripple" />}
      </div>

      <span className={`fp-status-pill ${pillStatus}`}>
        <span className="dot" />
        {STATUS_PILLS[pillStatus] || pillStatus}
      </span>

      {displayLabel && (
        <p className={`fp-label${status === 'error' ? ' error' : ''}`}>
          {displayLabel}
        </p>
      )}

      {simulateMode && (
        <span className="fp-simulate-note">⚠ Simulate mode — no real device</span>
      )}

      {onScan && (
        <button
          className="fp-scan-btn"
          onClick={onScan}
          disabled={isDisabled}
        >
          <Fingerprint size={16} />
          {status === 'scanning' ? 'Scanning…' : 'Scan Fingerprint'}
        </button>
      )}
    </div>
  );
}
